import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminData', JSON.stringify(response.data.admin));
      toast.success('Admin login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1762117360871-f11fbad00ee1?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.5)'
        }}
      />
      
      {/* Login Card */}
      <div className="glass-card rounded-2xl p-8 md:p-12 w-full max-w-md z-10">
        <div className="text-center mb-8">
          <div className="inline-block p-4 rounded-full bg-red-900/20 mb-4">
            <Shield className="text-red-600" size={48} />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Admin Portal
          </h1>
          <p className="text-stone-600">System Administration</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Email</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-red-600 focus:ring-0 rounded-lg text-lg px-4"
              required
            />
          </div>
          
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="h-14 bg-stone-50 border-2 border-stone-200 focus:border-red-600 focus:ring-0 rounded-lg text-lg px-4 pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-red-600 text-white rounded-full h-14 text-lg hover:bg-red-700 transition-transform hover:scale-105 active:scale-95 shadow-lg"
          >
            {loading ? 'Signing in...' : 'Admin Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
